import { useEffect } from 'react'
import { connect } from 'react-redux'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { webStorage, usePrevious } from 'sidecar-utility-library'

export const identifyMember = token => {
  const member = JSON.parse(webStorage.getItem('sh:member'))
  // the contexts here are defined by the "kinds" in the LD dashboard.
  if (token && member) {
    // by the time the user gets here we have most of their info to
    // create the user context so we just create that all at once.
    const userIdentity = {
      kind: 'multi',
      user: {
        key: member.uuid,
        dateOfBirth: member.dateOfBirth,
        gender: member.gender,
        primary: member.primary,
      },
    }

    // the mailing address may not be complete yet so we need to check
    // each value before we set it in the location context.
    const addr = member.mailingAddress
    if (addr) {
      userIdentity.location = {}
    }
    if (addr?.zip) {
      userIdentity.location['key'] = addr.zip
    }
    if (addr?.city) {
      userIdentity.location['city'] = addr.city
    }
    if (addr?.state) {
      userIdentity.location['state'] = addr.state
    }

    // the user may not be finished purchasing a policy so we need to
    // only add what we actually know about the policy in the product context.
    const pol = member.policy
    if (pol) {
      userIdentity.product = {}
    }
    if (pol?.uuid) {
      userIdentity.product['key'] = pol.uuid
    }
    if (pol?.offering) {
      userIdentity.product['offering'] = pol.offering
    }
    if (pol?.insuranceProduct) {
      userIdentity.product['insuranceProduct'] = pol.insuranceProduct
    }
    if (pol?.purchaseDate) {
      userIdentity.product['purchaseDate'] = pol.purchaseDate
    }
    if (pol?.startDate) {
      userIdentity.product['startDate'] = pol.startDate
    }
    if (pol?.paymentSchedule) {
      userIdentity.product['paymentSchedule'] = pol.paymentSchedule
    }
    if (pol?.expirationDate) {
      userIdentity.product['expirationDate'] = pol.expirationDate
    }
    if (pol?.city) {
      userIdentity.product['city'] = pol.address.city
    }
    if (pol?.state) {
      userIdentity.product['state'] = pol.address.state
    }
    if (pol?.zip) {
      userIdentity.product['zip'] = pol.address.zip
    }
    if (pol?.coverageStatus) {
      userIdentity.product['coverageStatus'] = pol.coverageStatus
    }
    if (pol?.packageName) {
      userIdentity.product['packageName'] = pol.packageName
    }
    if (pol?.insuranceFilingUuid) {
      userIdentity.product['insuranceFilingUuid'] = pol.insuranceFilingUuid
    }

    // not all members will be part of a group, but if they are, we need
    // to set up the group context.
    if (member.asoGroupDto) {
      userIdentity.group = {
        key: member.asoGroupDto.uuid,
        name: member.asoGroupDto.name,
      }
    }

    // when an admin is impersonating, include their identity so LD can
    // target feature flags by admin key.
    const adminUuid = webStorage.getItem('adminUuid')
    if (adminUuid) {
      userIdentity.admin = { key: adminUuid }
    }

    return userIdentity
  }
  return null
}

// this component is a hacky way to make it so that the App compeonent
// doesn't render a million times because of withAuthedUser. That HOC
// renders a lot so this component here is taking the brunt of that so App
// doesn't have to load over and over. We're doing this so that we can
// detect when a user is logged in, then identify them in LD.
const ContextIdentifier = ({ token, member }) => {
  const ldClient = useLDClient()

  // keep track of the previous token so we can tell when user logs out
  const prevToken = usePrevious(token)

  useEffect(() => {
    // we really only want to trigger this if the user is logging out
    // because by default the user is already identified as anon when
    // the LD context is initialized. We can tell this happens if the
    // token is null but the prevToken is not null.
    if (!token && prevToken) {
      const userIdentity = {
        key: webStorage.getItem('ipAddress'),
        kind: 'user',
        anonymous: true,
      }
      // calling this identify on load will cause LD to throw a CORS error that
      // is not really a CORS error. We were told by LD that it happens when
      // identity is called too close together and this would be the case here.
      ldClient.identify(userIdentity)
    }

    const userIdentity = identifyMember(token)
    if (userIdentity) {
      ldClient.identify(userIdentity)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ldClient is stable, prevToken/token intentionally omitted to only re-identify when member data changes
  }, [member])

  return null
}

export const LDContextIdentifier = connect(({ Auth }) => ({
  token: Auth.authToken,
  member: Auth.authedUser,
}))(ContextIdentifier)
